import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle, Clock, Mail, Loader2 } from 'lucide-react';
import * as React from 'react';
import { authClient, useSession } from '@/lib/auth-client';
import { AuthLayout } from '@/components/auth/auth-layout';
import { Button } from '@/components/ui/button';
import { maskEmail } from '@/lib/email-utils';

const verifyEmailSearchSchema = z.object({
  error: z.string().optional(),
});

export const Route = createFileRoute('/verify-email')({
  validateSearch: verifyEmailSearchSchema,
  component: VerifyEmailPage,
});

// BA verify-email scenarios:
// - Token valid, first verification → redirect to callbackURL, session has emailVerified=true → 'success'
// - Token valid, already verified → redirect to callbackURL, same as above → 'success'
// - Token expired → redirect with ?error=TOKEN_EXPIRED → 'expired'
// - Token invalid → redirect with ?error=INVALID_TOKEN → 'invalid'
type VerifyStatus =
  | 'checking'
  | 'success'
  | 'expired'
  | 'invalid'
  | 'check-email'
  | 'not-signed-in';

function VerifyEmailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { error } = Route.useSearch();
  const { data: session, isPending } = useSession();
  const [status, setStatus] = React.useState<VerifyStatus>('checking');
  const [resendCooldown, setResendCooldown] = React.useState(0);
  const [isResending, setIsResending] = React.useState(false);

  // After redirect from BA verification endpoint (no ?error=), the cookie cache
  // may still serve a stale session with emailVerified=false.
  // Force a fresh session fetch bypassing the cookie cache.
  const [freshSessionChecked, setFreshSessionChecked] = React.useState(false);

  React.useEffect(() => {
    if (error || freshSessionChecked) return;

    // No error param means we likely came from a successful verification redirect.
    // Force-refresh the session to bypass Better Auth's cookie cache.
    authClient
      .getSession({
        fetchOptions: { headers: { 'cache-control': 'no-cache' } },
      })
      .then(() => {
        setFreshSessionChecked(true);
      })
      .catch(() => {
        setFreshSessionChecked(true);
      });
  }, [error, freshSessionChecked]);

  // Determine status based on session and URL params
  React.useEffect(() => {
    if (isPending) return;
    // Wait for fresh session fetch when coming from verification redirect
    if (!error && !freshSessionChecked) return;

    if (error) {
      // BA error codes: TOKEN_EXPIRED, INVALID_TOKEN, USER_NOT_FOUND
      if (error === 'TOKEN_EXPIRED') {
        setStatus('expired');
      } else {
        // INVALID_TOKEN, USER_NOT_FOUND, or any unknown error
        setStatus('invalid');
      }
      return;
    }

    // BA redirects without ?error= when user.emailVerified is already true
    // (token already used). With autoSignInAfterVerification, user is signed in.
    // So "already verified" maps to: session exists + emailVerified=true (handled below as 'success').

    if (session?.user?.emailVerified) {
      setStatus('success');
      // Broadcast to other tabs
      try {
        const channel = new BroadcastChannel('email-verification');
        channel.postMessage({ type: 'email-verified' });
        channel.close();
      } catch {
        // BroadcastChannel not supported
      }
      // Auto-redirect to dashboard after 3s
      const timer = setTimeout(() => {
        navigate({ to: '/dashboard' as '/' });
      }, 3000);
      return () => clearTimeout(timer);
    }

    if (session?.user) {
      // Signed in but email not yet verified
      setStatus('check-email');
    } else {
      // Not signed in and no error — direct navigation or failed auto-sign-in
      setStatus('not-signed-in');
    }
  }, [isPending, session, error, navigate, freshSessionChecked]);

  // Resend cooldown timer
  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleResend = async () => {
    const email = session?.user?.email;
    if (!email) return;
    setIsResending(true);
    try {
      // No callbackURL here — the backend sendVerificationEmail callback
      // always appends the correct FRONTEND_URL-based callbackURL.
      await authClient.sendVerificationEmail({ email });
      setResendCooldown(60);
    } catch {
      // Silent fail — anti-enumeration
    } finally {
      setIsResending(false);
    }
  };

  const maskedEmail = session?.user?.email ? maskEmail(session.user.email) : '';

  if (isPending || status === 'checking') {
    return (
      <AuthLayout title={t('verification.title')} description={t('verification.checking')}>
        <div className="flex justify-center py-8">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </AuthLayout>
    );
  }

  // Scenario 1: Token OK — email verified successfully
  if (status === 'success') {
    return (
      <AuthLayout
        title={t('verification.successTitle')}
        description={t('verification.successDescription')}
      >
        <div className="flex flex-col items-center gap-4 py-6">
          <div className="flex size-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-950/50">
            <CheckCircle2 className="size-8 text-green-600 dark:text-green-400" />
          </div>
          <p className="text-sm text-muted-foreground">{t('verification.redirecting')}</p>
        </div>
        <Button
          className="w-full cursor-pointer"
          size="lg"
          onClick={() => navigate({ to: '/dashboard' as '/' })}
        >
          {t('verification.goToDashboard')}
        </Button>
      </AuthLayout>
    );
  }

  // Scenario 2: Token expired — offer to resend
  if (status === 'expired') {
    return (
      <AuthLayout
        title={t('verification.expiredTitle')}
        description={t('verification.expiredDescription')}
      >
        <div className="flex flex-col items-center gap-4 py-6">
          <div className="flex size-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950/50">
            <Clock className="size-8 text-amber-600 dark:text-amber-400" />
          </div>
        </div>
        {session?.user?.email ? (
          <ResendButton
            isResending={isResending}
            resendCooldown={resendCooldown}
            onResend={handleResend}
          />
        ) : (
          <Button
            className="w-full cursor-pointer"
            size="lg"
            onClick={() => navigate({ to: '/login' })}
          >
            {t('verification.goToLogin')}
          </Button>
        )}
      </AuthLayout>
    );
  }

  // Scenario 3: Token invalid
  if (status === 'invalid') {
    return (
      <AuthLayout
        title={t('verification.invalidTitle')}
        description={t('verification.invalidDescription')}
      >
        <div className="flex flex-col items-center gap-4 py-6">
          <div className="flex size-16 items-center justify-center rounded-full bg-destructive/10">
            <XCircle className="size-8 text-destructive" />
          </div>
        </div>
        <Button
          className="w-full cursor-pointer"
          size="lg"
          onClick={() => navigate({ to: '/login' })}
        >
          {t('verification.goToLogin')}
        </Button>
      </AuthLayout>
    );
  }

  // Not signed in — redirect to login
  if (status === 'not-signed-in') {
    return (
      <AuthLayout
        title={t('verification.notSignedInTitle')}
        description={t('verification.notSignedInDescription')}
      >
        <div className="flex flex-col items-center gap-4 py-6">
          <div className="flex size-16 items-center justify-center rounded-full bg-muted">
            <Mail className="size-8 text-muted-foreground" />
          </div>
        </div>
        <Button
          className="w-full cursor-pointer"
          size="lg"
          onClick={() => navigate({ to: '/login' })}
        >
          {t('verification.goToLogin')}
        </Button>
      </AuthLayout>
    );
  }

  // Default: check-email — user signed in but not yet verified
  return (
    <AuthLayout
      title={t('verification.checkEmailTitle')}
      description={t('verification.checkEmailDescription', { email: maskedEmail })}
    >
      <div className="flex flex-col items-center gap-4 py-6">
        <div className="flex size-16 items-center justify-center rounded-full bg-primary/10">
          <Mail className="size-8 text-primary" />
        </div>
      </div>
      <ResendButton
        isResending={isResending}
        resendCooldown={resendCooldown}
        onResend={handleResend}
        variant="outline"
      />
      <div className="mt-4 text-center">
        <Link
          to="/login"
          className="text-sm text-muted-foreground underline-offset-4 hover:underline"
        >
          {t('verification.backToLogin')}
        </Link>
      </div>
    </AuthLayout>
  );
}

function ResendButton({
  isResending,
  resendCooldown,
  onResend,
  variant = 'default',
}: {
  isResending: boolean;
  resendCooldown: number;
  onResend: () => void;
  variant?: 'default' | 'outline';
}) {
  const { t } = useTranslation();

  return (
    <Button
      variant={variant}
      className="w-full cursor-pointer"
      size="lg"
      disabled={isResending || resendCooldown > 0}
      onClick={onResend}
    >
      {isResending ? (
        <>
          <Loader2 className="size-4 animate-spin" />
          {t('verification.sending')}
        </>
      ) : resendCooldown > 0 ? (
        t('verification.resendCooldown', { seconds: resendCooldown })
      ) : (
        t('verification.resendLink')
      )}
    </Button>
  );
}
